import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

function money(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

const statusConfig = {
  IN_TRANSIT:  { emoji: '🚛', color: '#2563eb', bg: '#dbeafe', label: 'In Transit' },
  PICKED_UP:   { emoji: '📦', color: '#d97706', bg: '#fef3c7', label: 'Picked Up' },
  IN_PROGRESS: { emoji: '🔄', color: '#7c3aed', bg: '#ede9fe', label: 'In Progress' },
  DELIVERED:   { emoji: '✅', color: '#059669', bg: '#d1fae5', label: 'Delivered' },
  COMPLETED:   { emoji: '✅', color: '#059669', bg: '#d1fae5', label: 'Completed' },
  CLOSED:      { emoji: '📁', color: '#6b7280', bg: '#f3f4f6', label: 'Closed' },
  TENDERED:    { emoji: '📋', color: '#0891b2', bg: '#cffafe', label: 'Tendered' },
  POSTED:      { emoji: '📢', color: '#dc2626', bg: '#fee2e2', label: 'Posted' },
  CREATED:     { emoji: '✏️', color: '#6b7280', bg: '#f3f4f6', label: 'Created' },
};

export default function ShipperDashboard() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { shipperEmail } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!shipperEmail) return;
    setLoading(true);
    fetch(`${apiBase}/shipper/dashboard`, {
      headers: { 'x-shipper-email': shipperEmail }
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(d => { setData(d); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiBase, shipperEmail]);

  if (!shipperEmail) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
      Please set a Shipper Email in the dev panel to access the portal.
    </div>
  );

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading dashboard...</div>;
  if (error) return <div style={{ padding: 20, background: '#fef2f2', borderRadius: 10, color: '#991b1b' }}>{error}</div>;
  if (!data) return null;

  const { stats, recentLoads, shipper } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%)',
        borderRadius: 16, padding: '28px 32px', marginBottom: 24, color: 'white',
        position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'absolute', bottom: -60, right: 100, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }} />
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: '-0.5px' }}>
          Welcome back, {shipper.name}
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: 14, opacity: 0.7 }}>
          Shipment Portal • Real-time visibility into your freight
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Active Shipments', value: stats.activeLoads, color: '#2563eb', icon: '🚛', action: () => nav('/app/shipper/tracking') },
          { label: 'Pending', value: stats.pendingLoads, color: '#d97706', icon: '⏳' },
          { label: 'Delivered', value: stats.deliveredLoads, color: '#059669', icon: '✅' },
          { label: 'Total Spend', value: money(stats.totalSpendCents), color: '#7c3aed', icon: '💰', isText: true },
        ].map(card => (
          <div key={card.label} onClick={card.action}
            style={{
              background: 'white', borderRadius: 14, padding: '20px 18px',
              border: '1px solid #e5e7eb', cursor: card.action ? 'pointer' : 'default',
              transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}
            onMouseEnter={e => { if (card.action) e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { if (card.action) e.currentTarget.style.transform = ''; }}
          >
            <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>{card.icon} {card.label}</div>
            <div style={{ fontSize: card.isText ? 22 : 32, fontWeight: 900, color: card.color, marginTop: 6 }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        <button onClick={() => nav('/app/shipper/tracking')}
          style={{ padding: '12px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          📍 Track Shipments
        </button>
        <button onClick={() => nav('/app/shipper/loads')}
          style={{ padding: '12px 20px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          📦 View All Loads
        </button>
        <button onClick={() => nav('/app/shipper/create')}
          style={{ padding: '12px 20px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          ➕ Request Shipment
        </button>
      </div>

      {/* Recent Loads */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Recent Shipments</h2>
          <button onClick={() => nav('/app/shipper/loads')}
            style={{ fontSize: 13, color: '#2563eb', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
            View All →
          </button>
        </div>
        {recentLoads.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No shipments yet</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['LOAD #', 'ROUTE', 'STATUS', 'CARRIER', 'ETA'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentLoads.map(load => {
                const sc = statusConfig[load.status] || { emoji: '❓', color: '#6b7280', bg: '#f3f4f6', label: load.status };
                return (
                  <tr key={load.id} onClick={() => nav(`/app/shipper/loads/${load.id}`)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px 16px', fontWeight: 700, fontSize: 14 }}>{load.loadNumber}</td>
                    <td style={{ padding: '14px 16px', fontSize: 14 }}>
                      {load.originCity}, {load.originState} → {load.destCity}, {load.destState}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.color }}>
                        {sc.emoji} {sc.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: '#6b7280' }}>{load.carrierName || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: '#6b7280' }}>
                      {load.eta ? new Date(load.eta).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
